import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  Box,
  DialogContent,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import {
  ProgressButton,
  DialogRoot,
  SquareIconButton,
  useToast
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";

import { ChangeEvent, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import envConstants from "~/config/envConstants";
import {
  setIsOosModalOpenedAfterQtyModal,
  setOutOfStockDialogStatus
} from "~/features/autostorePicking/outOfStockDialog/outOfStockDialog.slice";

import { displayPickData } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { QUANTITY_MAX_VALUE } from "~/lib/shared";
import { setIsPickQuantityConfirmed } from "~/redux/actions";
import { selectAbleQtyToPick } from "~/redux/selectors/outOfStockSelectors";
import {
  selectSitePortId,
  selectWorkstationAutostoreGridId,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import { useGetFocusedPickQuery } from "~/redux/warehouse/autostorePicking.hooks";

import { setPickedQuantityModalStatus } from "./confirmPickQuantityModal.slice";

type ConfirmPickQuantityModalProps = {
  open: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg";
};

export function ConfirmPickQuantityModal(props: ConfirmPickQuantityModalProps) {
  const { open, maxWidth } = props;
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const [enteredQuantity, setEnteredQuantity] = useState<number | string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const workstationId = useAppSelector(selectWorkstationId);
  const sitePortId = useAppSelector(selectSitePortId);
  const selectedAutostoreGridId = useAppSelector(
    selectWorkstationAutostoreGridId
  );
  const binId = useAppSelector(
    (state) => state.confirmPickQuantityModalSlice.selectedBinId
  );
  const ableToPickQuantity = useAppSelector(selectAbleQtyToPick);
  const trackedModalName = useMixpanelModalName("Picking Confirm Quantity");

  const { data: focusedPick } = useGetFocusedPickQuery(
    workstationId ? { workstationId: workstationId } : skipToken,
    {
      refetchOnMountOrArgChange: true
    }
  );

  const pickedQuantity = ableToPickQuantity || focusedPick?.quantity.value;

  const onConfirmCallback = () => {
    dispatch(setIsPickQuantityConfirmed(true));
    if (focusedPick && sitePortId) {
      void displayPickData({
        portId: sitePortId,
        pickId: focusedPick.pickId,
        batchId: focusedPick.batchId,
        gridId: selectedAutostoreGridId,
        quantity: ableToPickQuantity || undefined
      });
    }
  };

  return (
    <DialogRoot open={open} maxWidth={maxWidth}>
      <Paper sx={{ p: 4 }}>
        <DialogContent
          sx={{
            "@media (min-width: 1000px)": {
              minWidth: "500px"
            }
          }}
        >
          <Stack gap={4}>
            <Typography variant="h4">{t("please enter quantity")}</Typography>
            <Box position="relative">
              <Stack direction="row" gap={1}>
                <TextField
                  variant="outlined"
                  value={enteredQuantity}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const parseInput = parseInt(e.target.value, 10);
                    if (parseInput > QUANTITY_MAX_VALUE) {
                      return;
                    }
                    if (parseInput > 0) {
                      setEnteredQuantity(parseInput);
                    } else if (parseInput < 1) {
                      errorToast(t("your input must be a positive number"));
                    }
                    if (Number.isNaN(parseInput)) {
                      setEnteredQuantity("");
                    }
                  }}
                  onBlur={() => {
                    if (enteredQuantity !== "") {
                      mixpanelTrack({
                        trackedPageName: trackedModalName,
                        type: "Input Change",
                        label: "Enter Quantity",
                        eventProperties: {
                          Quantity: enteredQuantity
                        }
                      });
                    }
                  }}
                  fullWidth
                  placeholder={t("enter quantity")}
                  type="number"
                  autoFocus
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: {
                      fontSize: "20px",
                      padding: "35px 10px 35px 18px"
                    }
                  }}
                  inputRef={inputRef}
                />

                <Stack gap={1.5} direction="column" justifyContent="center">
                  <SquareIconButton
                    aria-label="increase quantity"
                    onClick={(): void => {
                      if (typeof enteredQuantity === "number") {
                        const newQty = enteredQuantity + 1;
                        if (newQty > QUANTITY_MAX_VALUE) return;
                        mixpanelTrack({
                          trackedPageName: trackedModalName,
                          type: "Button Click",
                          label: "Increase Quantity",
                          eventProperties: {
                            Quantity: newQty
                          }
                        });
                        setEnteredQuantity(newQty);
                      } else if (
                        typeof enteredQuantity === "string" &&
                        enteredQuantity.length === 0
                      ) {
                        mixpanelTrack({
                          trackedPageName: trackedModalName,
                          type: "Button Click",
                          label: "Increase Quantity",
                          eventProperties: {
                            Quantity: 1
                          }
                        });
                        setEnteredQuantity(1);
                      }
                    }}
                    disabled={
                      typeof enteredQuantity === "number" &&
                      enteredQuantity >= QUANTITY_MAX_VALUE
                    }
                  >
                    <ExpandLessIcon
                      style={{
                        fontSize: 22
                      }}
                    />
                  </SquareIconButton>
                  <SquareIconButton
                    aria-label="decrease quantity"
                    onClick={(): void => {
                      if (
                        typeof enteredQuantity === "number" &&
                        enteredQuantity > 1
                      ) {
                        mixpanelTrack({
                          trackedPageName: trackedModalName,
                          type: "Button Click",
                          label: "Decrease Quantity",
                          eventProperties: {
                            Quantity: enteredQuantity - 1
                          }
                        });
                        setEnteredQuantity((prevEnteredQuantity) => {
                          if (typeof prevEnteredQuantity === "number") {
                            return prevEnteredQuantity - 1;
                          }
                          return enteredQuantity - 1;
                        });
                      } else if (
                        (typeof enteredQuantity === "number" &&
                          enteredQuantity <= 1) ||
                        (typeof enteredQuantity === "string" &&
                          enteredQuantity.length === 0)
                      ) {
                        errorToast(t("your input must be a positive number"));
                      }
                    }}
                  >
                    <ExpandMoreIcon
                      style={{
                        fontSize: 22
                      }}
                    />
                  </SquareIconButton>
                </Stack>
              </Stack>
            </Box>

            <Stack flexDirection={"row"} justifyContent={"center"} gap={4}>
              {[2, 3, 4, 5].map((num) => (
                <ProgressButton
                  key={`quick-confirm-quantity-${num}`}
                  emphasis="low"
                  variant="contained"
                  color="primary"
                  sx={{
                    backgroundColor:
                      envConstants.DEV_CHEATS_STAGING === "true" &&
                      num !== pickedQuantity
                        ? "gray"
                        : null,
                    width: "75px",
                    height: "75px"
                  }}
                  onClick={() => {
                    mixpanelTrack({
                      trackedPageName: trackedModalName,
                      type: "Button Click",
                      label: "Quick Select Quantity",
                      eventProperties: {
                        Quantity: num
                      }
                    });
                    setEnteredQuantity(num);
                  }}
                >
                  <Typography>{num}</Typography>
                </ProgressButton>
              ))}
            </Stack>
            <Stack flexDirection="row" justifyContent="center" gap={10}>
              <ProgressButton
                buttonSize="medium"
                emphasis="high"
                responsive
                variant="contained"
                color="error"
                fullWidth
                onClick={(): void => {
                  mixpanelTrack({
                    trackedPageName: trackedModalName,
                    type: "Button Click",
                    label: "Out of Stock"
                  });
                  setEnteredQuantity("");
                  dispatch(setIsOosModalOpenedAfterQtyModal(true));
                  dispatch(setOutOfStockDialogStatus(true));
                  if (pickedQuantity) {
                    dispatch(
                      setPickedQuantityModalStatus({
                        isShown: false,
                        pickedQuantity: pickedQuantity,
                        selectedBinId: binId
                      })
                    );
                  }
                }}
                sx={{
                  height: "50px",
                  width: "150px",
                  "@media (min-width: 600px)": {
                    height: "70px",
                    width: "200px"
                  }
                }}
              >
                <Typography>{t("out of stock")}</Typography>
              </ProgressButton>
              <ProgressButton
                onClick={() => {
                  if (pickedQuantity && enteredQuantity !== pickedQuantity) {
                    errorToast(
                      t("quantity doesn't match expected quantity", {
                        expectedQuantity: pickedQuantity
                      })
                    );

                    inputRef.current?.focus();
                  } else {
                    mixpanelTrack({
                      trackedPageName: trackedModalName,
                      type: "Button Click",
                      label: "Confirm",
                      eventProperties: {
                        "Entered Quantity": enteredQuantity,
                        "Expected Quantity": pickedQuantity
                      }
                    });
                    onConfirmCallback();
                    setEnteredQuantity("");
                    dispatch(
                      setPickedQuantityModalStatus({
                        isShown: false,
                        pickedQuantity: null,
                        selectedBinId: null
                      })
                    );
                  }
                }}
                buttonSize="medium"
                emphasis="high"
                responsive
                variant="contained"
                color="primary"
                sx={{
                  height: "50px",
                  width: "150px",
                  "@media (min-width: 600px)": {
                    height: "70px",
                    width: "200px"
                  }
                }}
              >
                <Typography>{t("confirm")}</Typography>
              </ProgressButton>
            </Stack>
          </Stack>
        </DialogContent>
      </Paper>
    </DialogRoot>
  );
}
